import { PlanData, NormalizedUnit, AutoUnitConfig, ComplementaryAutoUnit } from "../types/types";

const AUTO_UNIT_PREFIX = "__auto_unit__";

const AUTO_UNIT_CONFIG: Record<string, AutoUnitConfig> = {
  "__auto_unit__kg": { label: "KG", conversionFactor: 1000 },
  "__auto_unit__g": { label: "G", conversionFactor: 0.001 },
  "__auto_unit__ml": { label: "ML", conversionFactor: 0.001 },
  "__auto_unit__l": { label: "L", conversionFactor: 1000 },
};

const UOM_MAPPINGS: Record<NormalizedUnit, string[]> = {
  G: ["G", "GR", "GRM", "GRAM", "GRAMS"],
  KG: ["KG", "KGR", "KILOGRAM", "KILOGRAMS"],
  L: ["L", "LTR", "LITRE", "LITER", "LITRES", "LITERS"],
  ML: ["ML", "MLTR", "MILLILITRE", "MILLILITER", "MILLILITRES", "MILLILITERS"],
};

const COMPLEMENT_MAP: Record<NormalizedUnit, string> = {
  G: "__auto_unit__kg",
  KG: "__auto_unit__g",
  ML: "__auto_unit__l",
  L: "__auto_unit__ml",
};

export function normalizeUomCode(uomCode: string | undefined | null): NormalizedUnit | null {
  if (!uomCode) return null;
  const upper = uomCode.toUpperCase();
  
  for (const [normalized, codes] of Object.entries(UOM_MAPPINGS)) {
    if (codes.includes(upper)) return normalized as NormalizedUnit;
  }
  return null;
}

export function isConvertibleUom(uomCode: string | undefined | null): boolean {
  return normalizeUomCode(uomCode) !== null;
}

export function getComplementaryAutoUnit(uomCode: string | undefined | null): ComplementaryAutoUnit | null {
  const normalized = normalizeUomCode(uomCode);
  if (!normalized) return null;

  const autoUnitId = COMPLEMENT_MAP[normalized];
  const config = AUTO_UNIT_CONFIG[autoUnitId];
  
  return config ? { autoUnitId, ...config } : null;
}

export function isAutoGeneratedUnit(displayContainerId: string | undefined | null): boolean {
  return !!displayContainerId && displayContainerId.startsWith(AUTO_UNIT_PREFIX);
}

function getAutoUnitConfig(displayContainerId: string): AutoUnitConfig | null {
  return AUTO_UNIT_CONFIG[displayContainerId] ?? null;
}

export function getTaskDisplayName(planData: PlanData): string {
  if (planData.itemSalesPlanDtos[0]?.uomCode === null || planData.itemSalesPlanDtos[0]?.uomCode === "each" ) {
    return "PLATE";
  }
  return planData.taskData?.taskNameLocal || "PREPARE";
}


export function getTotalPlannedVolume(planData: PlanData): number {
  const total = planData.itemSalesPlanDtos.reduce(
    (sum, dto) => sum + (dto.plannedVol || 0),
    0,
  );
  return Math.round(total * 10) / 10;
}


export function getTotalProducedVolume(planData: PlanData): number {
  const total = planData.itemSalesPlanDtos.reduce(
    (sum, dto) => sum + (dto.producedVol || 0),
    0,
  );
  return Math.round(total * 10) / 10;
}

export function getAvailableVolume(planData: PlanData): number {
  if (!hasAvailableInput(planData)) return 0;
  const availableVol = planData.itemSalesPlanDtos.find(
    dto => dto.availableVol != null && dto.availableVol !== 0
  )?.availableVol || 0;
  return Math.round(availableVol * 10) / 10;
}

export function isStartingStockSet(planData: PlanData): boolean {
  if (!hasAvailableInput(planData)) return false;
  return planData.itemSalesPlanDtos.some(
    dto => dto.availableVol != null && dto.availableVol !== 0
  );
}

export function getRemainingVolume(planData: PlanData): number {
  const planned = getTotalPlannedVolume(planData);
  const produced = hasProducedInput(planData) ? getTotalProducedVolume(planData) : 0;
  const available = getAvailableVolume(planData);
  const remaining = planned - produced - available;
  return Math.round(remaining * 10) / 10;
}

export function getStatusFromCode(statusCode: string): string {
  const code = parseInt(statusCode, 10);
  if (code === 99) return "done";
  if (code === 20) return "inProgress";
  return "todo";
}

export type TransitionModalType = "none" | "produceQuantity" | "warning" | "blocked";

export function canTransition(from: string, to: string): boolean {
  if (from === to) return false;
  if (from === "done" && to === "todo") return false;
  return true;
}

export function getTransitionModalType(
  from: string,
  to: string,
  producedVol: number,
  availableVol: number,
  plannedVol: number
): TransitionModalType {
  if (from === to) return "blocked";
  if (from === "done" && to === "todo") return "blocked";
  if (from === "todo" && to === "inProgress") return "none";
  if (from === "todo" && to === "done") return "produceQuantity";
  if (from === "inProgress" && to === "done") {
    const totalCompleted = producedVol + availableVol;
    if (totalCompleted >= plannedVol) {
      return "none";
    }
    return "produceQuantity";
  }
  if (from === "inProgress" && to === "todo") {
    if (producedVol > 0) {
      return "warning";
    }
    return "none";
  }
  
  if (from === "done" && to === "inProgress") return "none";
  
  return "none";
}

export function getDisplayConversion(planData: PlanData): { conversionFactor: number; displayUom: string } | null {
  if (!planData.displayContainerId) return null;

  // Check if it's an auto-generated unit
  if (isAutoGeneratedUnit(planData.displayContainerId)) {
    const autoConfig = getAutoUnitConfig(planData.displayContainerId);
    if (autoConfig) {
      return {
        conversionFactor: autoConfig.conversionFactor,
        displayUom: autoConfig.label,
      };
    }
    return null;
  }

  // Check for custom container conversion
  const conversion = planData.containerConversionDtos?.find(
    (c) => c.containerId === planData.displayContainerId
  );

  if (!conversion || !conversion.containerConversion) return null;

  return {
    conversionFactor: conversion.containerConversion,
    displayUom: String(conversion.containerName),
  };
}

export function getStatusCode(status: string): string {
  if (status === "done") return "99";
  if (status === "inProgress") return "20";
  return "10";
}

export function formatVolume(value: number): string {
  return value.toFixed(1);
}

// Keeps digits plus at most one decimal separator (dot or comma, always normalized
// to a dot) that must follow a digit, and at most one decimal.
export function sanitizeDecimalInput(value: string): string {
  let result = "";
  let hasSeparator = false;
  let decimals = 0;

  for (const char of value) {
    if (char >= "0" && char <= "9") {
      if (hasSeparator) {
        if (decimals >= 1) continue;
        decimals += 1;
      }
      result += char;
    } else if (
      (char === "." || char === ",") &&
      result.length > 0 &&
      !hasSeparator
    ) {
      hasSeparator = true;
      result += ".";
    }
  }

  return result;
}

export function hasTaskInput(planData: PlanData, description: string): boolean {
  return planData.itemMarketDetailTaskInputDtos?.some(
    (input) => input.description.toLowerCase() === description.toLowerCase()
  ) ?? false;
}

export function hasProducedInput(planData: PlanData): boolean {
  return hasTaskInput(planData, "Produced");
}

export function hasAvailableInput(planData: PlanData): boolean {
  return hasTaskInput(planData, "In stock");
}
